<template>
  <v-container fluid class="ccp-root pa-4">

    <!-- PAGE HEADER -->
    <div class="ccp-header mb-8">
      <div class="ccp-header-icon">
        <v-icon size="28" color="white">mdi-swap-horizontal-bold</v-icon>
      </div>
      <div class="ccp-header-text">
        <h1 class="ccp-title">{{ $t("compararPreciosProveedor.title") }}</h1>
        <p class="ccp-subtitle">
          Subí una lista de precios del proveedor y compará contra los precios actuales del sistema
        </p>
      </div>
    </div>

    <!-- CONTENT CARD -->
    <v-card class="ccp-card" outlined>
      <v-window v-model="step" touchless class="ccp-window">

        <!-- Step 1: Configuracion -->
        <v-window-item :value="1">
          <div class="ccp-step-body">
            <div class="ccp-step-heading">
              <div class="step-bubble">1</div>
              <div>
                <div class="step-heading-title">{{ $t("compararPreciosProveedor.step1") }}</div>
                <div class="step-heading-sub">Seleccioná el proveedor y el tipo de archivo</div>
              </div>
            </div>
            <div class="ccp-step-content">
              <paso-configuracion-inicial ref="paso1" @error="onError" @proveedor-change="paso1ProveedorId = $event" />
            </div>
          </div>
        </v-window-item>

        <!-- Step 2: Mapeo columnas (wide) -->
        <v-window-item :value="2">
          <div class="ccp-step-body ccp-step-body--wide">
            <div class="ccp-step-heading px-8 mb-0">
              <div class="step-bubble">2</div>
              <div>
                <div class="step-heading-title">{{ $t("compararPreciosProveedor.step2Archivo") }}</div>
                <div class="step-heading-sub">Subí el archivo y mapeá cada columna al campo correspondiente</div>
              </div>
            </div>
            <div class="ccp-step-content px-8">
              <paso-mapeo-columnas ref="paso2" :modo="config.modo" @error="onError" />
            </div>
          </div>
        </v-window-item>

        <!-- Step 3: Resultado analisis (wide, sin padding) -->
        <v-window-item :value="3">
          <div class="ccp-step-body ccp-step-body--wide">
            <div class="ccp-step-heading px-8 mb-0">
              <div class="step-bubble step-bubble--success">
                <v-icon small color="white">mdi-check</v-icon>
              </div>
              <div>
                <div class="step-heading-title">{{ $t("compararPreciosProveedor.step2") }}</div>
                <div class="step-heading-sub">Revisá las coincidencias, elegí qué actualizar y confirmá</div>
              </div>
            </div>
            <div class="ccp-step-content pa-0">
              <paso-resultado-analisis
                ref="paso3"
                :items="stepDataItems"
                :deposito-id="stepData && stepData.depositoId || null"
                :modo="config.modo"
                @update:cantidadParaAplicar="cantidadParaAplicar = $event"
              />
            </div>
          </div>
        </v-window-item>

      </v-window>

      <!-- Footer -->
      <v-divider />
      <div class="ccp-footer">
        <div class="ccp-footer-left">
          <v-btn v-if="step > 1" text color="grey darken-1" @click="goBack">
            <v-icon left small>mdi-arrow-left</v-icon>
            {{ $t("compararPreciosProveedor.back") }}
          </v-btn>
        </div>
        <div class="ccp-footer-right">
          <v-btn v-if="step === 1" color="primary" elevation="0" style="min-width: 140px" :disabled="!paso1ProveedorId" @click="avanzarPaso1">
            {{ $t("compararPreciosProveedor.continuar") }}
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn v-if="step === 2" color="primary" elevation="0" :loading="loadingPaso2" @click="avanzarPaso2">
            Aplicar mapeo
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 3"
            color="success"
            elevation="0"
            :disabled="!cantidadParaAplicar"
            @click="abrirModalConfirmacion"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            Aplicar {{ cantidadParaAplicar ? cantidadParaAplicar + ' cambio' + (cantidadParaAplicar !== 1 ? 's' : '') : 'cambios' }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Modal confirmacion -->
    <v-dialog v-model="modalConfirmar" max-width="420" persistent>
      <v-card style="border-radius: 14px;">
        <v-card-title class="text-h6 pt-5 px-6">
          {{ $t("compararPreciosProveedor.confirmarTitulo") }}
        </v-card-title>
        <v-card-text class="px-6">
          {{ $t("compararPreciosProveedor.confirmarMensaje", { n: cantidadParaAplicar }) }}
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn text color="grey darken-1" @click="modalConfirmar = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="loadingAplicar" @click="ejecutarAplicar">
            {{ $t("common.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarColor === 'error' ? 10000 : 4000" top right>
      <v-icon left small>{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
      <span style="white-space: pre-line;">{{ snackbarText }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import PasoConfiguracionInicial from "./PasoConfiguracionInicial.vue";
import PasoMapeoColumnas from "./PasoMapeoColumnas.vue";
import PasoResultadoAnalisis from "./PasoResultadoAnalisis.vue";
import CompararPreciosProveedorProvider from "../../providers/CompararPreciosProveedorProvider";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";

export default {
  name: "CompararPreciosProveedorPage",
  components: {
    PasoConfiguracionInicial,
    PasoMapeoColumnas,
    PasoResultadoAnalisis,
  },
  data() {
    return {
      step: 1,
      paso1ProveedorId: null,
      config: { modo: "SIMPLE", proveedorId: null },
      stepData: null,
      stepDataItems: [],
      cantidadParaAplicar: 0,
      loadingPaso2: false,
      loadingAplicar: false,
      modalConfirmar: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  methods: {
    goBack() {
      if (this.step > 1) this.step--;
    },
    avanzarPaso1() {
      if (!this.$refs.paso1.validate()) return;
      this.config = this.$refs.paso1.getData();
      this.step = 2;
    },
    async avanzarPaso2() {
      const rows = await this.$refs.paso2.procesar();
      if (!rows) return;

      let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
      if (!depositoId) {
        const resConfig = await UserConfigurationProvider.getUserConfiguration(true);
        depositoId = resConfig?.data?.userConfigurationGet?.depositoIdSeleccionado || null;
      }
      if (!depositoId) {
        this.onError(this.$t("compararPreciosProveedor.errorSinDeposito"));
        return;
      }

      this.loadingPaso2 = true;
      try {
        const res = await CompararPreciosProveedorProvider.compararPreciosProveedor(
          this.config.proveedorId,
          depositoId,
          rows
        );
        const items = res?.data?.compararPreciosProveedor || [];
        this.stepData = { proveedorId: this.config.proveedorId, depositoId, items };
        this.stepDataItems = items;
        this.cantidadParaAplicar = 0;
        this.step = 3;
      } catch (e) {
        const msg = e?.graphQLErrors?.[0]?.message || e?.message || this.$t("compararPreciosProveedor.errorProcesar");
        this.onError(msg);
      } finally {
        this.loadingPaso2 = false;
      }
    },
    abrirModalConfirmacion() {
      if (this.cantidadParaAplicar <= 0) return;
      this.modalConfirmar = true;
    },
    async ejecutarAplicar() {
      const paso3 = this.$refs.paso3;
      if (!paso3 || !this.stepData?.proveedorId) return;

      const items = paso3.getItemsParaAplicar();
      if (!items.length) {
        this.onError(this.$t("compararPreciosProveedor.errorSinItems"));
        this.modalConfirmar = false;
        return;
      }

      this.loadingAplicar = true;
      try {
        const res = await CompararPreciosProveedorProvider.aplicarComparacionPreciosProveedor(
          this.stepData.proveedorId,
          this.stepData.depositoId,
          items
        );
        const result = res?.data?.aplicarComparacionPreciosProveedor;
        if (result?.ok) {
          this.modalConfirmar = false;
          this.onSuccess(result.mensaje || this.$t("compararPreciosProveedor.success"));
        } else {
          this.onError(result?.mensaje || this.$t("compararPreciosProveedor.error"));
        }
      } catch (e) {
        const msg = e?.graphQLErrors?.[0]?.message || e?.message || this.$t("compararPreciosProveedor.error");
        this.onError(msg);
      } finally {
        this.loadingAplicar = false;
      }
    },
    onSuccess(msg) {
      this.snackbarText = msg || this.$t("compararPreciosProveedor.success");
      this.snackbarColor = "success";
      this.snackbar = true;
      this.$router.push({ name: "ProductoManagementPage" });
    },
    onError(msg) {
      this.snackbarText = msg || this.$t("compararPreciosProveedor.error");
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.ccp-root { max-width: 100%; }

/* ── Header ── */
.ccp-header {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ccp-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1976D2, #1565C0);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(25,118,210,0.35);
}
.ccp-title {
  font-size: 22px;
  font-weight: 700;
  color: #202124;
  margin: 0;
  line-height: 1.2;
}
.ccp-subtitle {
  font-size: 13px;
  color: #5F6368;
  margin: 4px 0 0;
}

/* ── Card ── */
.ccp-card {
  border-radius: 14px !important;
  border-color: #E8EAED !important;
}
.ccp-window { overflow: visible; }

/* ── Step body ── */
.ccp-step-body {
  padding: 28px 32px 24px;
}
.ccp-step-body--wide {
  padding-left: 0;
  padding-right: 0;
}
.ccp-step-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
}
.step-bubble {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #1976D2;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.step-bubble--success { background: #2E7D32; }
.step-heading-title {
  font-size: 15px;
  font-weight: 600;
  color: #202124;
  line-height: 1.3;
}
.step-heading-sub {
  font-size: 12.5px;
  color: #5F6368;
  margin-top: 2px;
}
.ccp-step-content {
  padding-left: 42px;
}

/* ── Footer ── */
.ccp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
}
.ccp-footer-left,
.ccp-footer-right {
  display: flex;
  align-items: center;
}
</style>
